import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  FiBriefcase, FiBook, FiShield, FiGlobe, 
  FiHeart, FiCoffee, FiCheckCircle, FiArrowRight,
  FiTrendingUp, FiDollarSign, FiClock, FiAward
} from 'react-icons/fi';

const Clients = () => {
  const clientCategories = [
    {
      icon: FiBriefcase,
      title: 'Corporate Offices & SMEs',
      description: 'Streamline your office supplies procurement with our comprehensive business solutions',
      benefits: [
        'Bulk discounts on regular orders',
        'Scheduled deliveries',
        'Dedicated account management',
        'Customized pricing packages',
      ],
      color: 'from-blue-400 to-blue-600',
    },
    {
      icon: FiBook,
      title: 'Educational Institutions',
      description: 'Schools, universities, and training centers benefit from our specialized education packages',
      benefits: [
        'Educational discounts',
        'Flexible payment terms',
        'End-of-term billing options',
        'Bulk ordering for new semesters',
      ],
      color: 'from-green-400 to-green-600',
    },
    {
      icon: FiShield,
      title: 'Government Agencies',
      description: 'Parastatals and government departments receive tender-compliant procurement services',
      benefits: [
        'Full tender compliance',
        'Official documentation',
        'Government payment terms',
        'Transparent bidding process',
      ],
      color: 'from-purple-400 to-purple-600',
    },
    {
      icon: FiGlobe,
      title: 'NGOs & International Agencies',
      description: 'Non-profit organizations and aid agencies get procurement that meets international standards',
      benefits: [
        'International standards compliance',
        'Donor requirement adherence',
        'Project-based procurement',
        'Detailed reporting',
      ],
      color: 'from-orange-400 to-orange-600',
    },
    {
      icon: FiHeart,
      title: 'Healthcare Facilities',
      description: 'Hospitals, clinics, and medical centers receive priority service for essential supplies',
      benefits: [
        'Medical-grade supplies',
        'Urgent delivery options',
        'Quality certifications',
        'Emergency order support',
      ],
      color: 'from-red-400 to-red-600',
    },
    {
      icon: FiCoffee,
      title: 'Hotels & Restaurants',
      description: 'Hospitality industry partners receive specialized equipment and consumable supplies',
      benefits: [
        'Specialized equipment sourcing',
        'Food-grade products',
        'Regular delivery schedules',
        'Seasonal supply management',
      ],
      color: 'from-yellow-400 to-yellow-600',
    },
  ];

  const whyChooseUs = [
    {
      icon: FiCheckCircle,
      title: 'One Invoice for All Supplies',
      description: 'Simplify your accounting with consolidated billing from a single supplier',
    },
    {
      icon: FiTrendingUp,
      title: 'Dedicated Account Manager',
      description: 'Your personal procurement specialist who understands your business needs',
    },
    {
      icon: FiDollarSign,
      title: 'Flexible Payment Terms',
      description: 'Credit facilities and payment plans tailored to your cash flow requirements',
    },
    {
      icon: FiClock,
      title: 'Same-Day Delivery Options',
      description: 'Fast delivery within Dar es Salaam for urgent supply needs',
    },
    {
      icon: FiAward,
      title: 'Quality Guarantee',
      description: 'Every product is inspected and comes with our quality assurance promise',
    },
    {
      icon: FiShield,
      title: 'Trusted Partnership',
      description: 'Join hundreds of satisfied clients who rely on us for their supply needs',
    },
  ];

  return (
    <>
      <Helmet>
        <title>Our Clients - NeoVam General Supplies</title>
        <meta name="description" content="Trusted by businesses and institutions across Tanzania. Discover how NeoVam General Supplies serves corporate offices, schools, government, NGOs, healthcare, and hospitality sectors." />
      </Helmet>

      <div className="min-h-screen pt-[120px]">
        {/* Hero Section */}
        <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-br from-primary-50 via-white to-primary-50 dark:from-dark-950 dark:via-dark-900 dark:to-dark-950">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6 animate-fade-in">
              Who <span className="text-gradient">We Serve</span>
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto animate-slide-up">
              Trusted by businesses and institutions across Tanzania. Discover how we can simplify your procurement process.
            </p>
          </div>
        </section>

        {/* Client Categories */}
        <section className="py-12 sm:py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {clientCategories.map((category, index) => (
                <div
                  key={index}
                  className="card hover:scale-105 animate-fade-in"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${category.color} flex items-center justify-center mb-4`}>
                    <category.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                    {category.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                    {category.description}
                  </p>
                  <div className="space-y-2 mb-6">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                      Benefits:
                    </h4>
                    {category.benefits.map((benefit, benefitIndex) => (
                      <div key={benefitIndex} className="flex items-start gap-2">
                        <FiCheckCircle className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {benefit}
                        </span>
                      </div>
                    ))}
                  </div>
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 text-primary-400 hover:text-primary-600 font-semibold transition-colors duration-300"
                  >
                    Get Started
                    <FiArrowRight />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Clients Choose Us */}
        <section className="py-12 sm:py-16 md:py-20 bg-gray-50 dark:bg-dark-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
                Why Clients <span className="text-gradient">Choose Us</span>
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                The advantages that make us the preferred procurement partner
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {whyChooseUs.map((reason, index) => (
                <div
                  key={index}
                  className="card text-center hover:scale-105 animate-slide-up"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <reason.icon className="w-12 h-12 text-primary-400 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    {reason.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {reason.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Success Stories Preview */}
        <section className="py-12 sm:py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Client <span className="text-gradient">Success Stories</span>
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                Real impact from our procurement partnerships
              </p>
            </div>
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="card animate-fade-in">
                <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mb-4">
                  <FiBriefcase className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  Manufacturing Excellence
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
                  "Partnering with NeoVam reduced our procurement costs by 20% and freed up our team to focus on core operations. Their reliability is unmatched."
                </p>
                <p className="text-sm font-semibold text-primary-400">
                  — Operations Director, Leading Manufacturing Company
                </p>
              </div>

              <div className="card animate-fade-in animate-delay-200">
                <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
                  <FiBook className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  Educational Efficiency
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
                  "Managing supplies for a university is complex. NeoVam simplified everything with one contact, one invoice, and consistently on-time delivery."
                </p>
                <p className="text-sm font-semibold text-primary-400">
                  — Procurement Manager, Major University
                </p>
              </div>

              <div className="card animate-fade-in animate-delay-300">
                <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
                  <FiHeart className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  Healthcare Support
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
                  "In healthcare, supply reliability is critical. NeoVam's urgent delivery service and quality products have been invaluable to our operations."
                </p>
                <p className="text-sm font-semibold text-primary-400">
                  — Hospital Administrator, Healthcare Facility
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Become a Client CTA */}
        <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-br from-primary-400 to-primary-600">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">
              Join Our Growing Family of Satisfied Clients
            </h2>
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              Experience the difference of working with a procurement partner who truly understands your needs. Let's simplify your supply chain together.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-primary-600 text-lg font-semibold rounded-xl hover:shadow-glow hover:scale-105 transition-all duration-300"
              >
                Become a Client
                <FiArrowRight />
              </Link>
              <Link
                to="/products"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-white text-white text-lg font-semibold rounded-xl hover:bg-white hover:text-primary-600 transition-all duration-300"
              >
                View Our Products
                <FiArrowRight />
              </Link>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 sm:py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                { value: '500+', label: 'Active Clients' },
                { value: '98%', label: 'Client Satisfaction' },
                { value: '10,000+', label: 'Orders Fulfilled' },
                { value: '6', label: 'Industry Sectors' },
              ].map((stat, index) => (
                <div key={index} className="text-center animate-fade-in" style={{ animationDelay: `${index * 100}ms` }}>
                  <div className="text-4xl sm:text-5xl font-bold text-gradient mb-2">
                    {stat.value}
                  </div>
                  <div className="text-lg text-gray-600 dark:text-gray-400">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Clients;
